import type { OrganizerBindingCatalogsResource } from '@/features/legends/api/resources/engine'
import type {
  LegendActivationResource,
  LegendErrorResource,
  LegendResource,
  SaveLegendVersionResultResource,
} from '@/features/legends/api/resources/legend'
import { typeFromValueType } from '@/features/legends/editor/ryno-types'
import type { Id } from '@/infra/id'
import { maybeParseDate } from '@/utils/date-util'
import type { LegendProps } from './Legend'
import type { LegendAnalyzerError, OrganizerBindings } from './legend-engine'
import type { LegendVersionProps } from './LegendVersion'

/**
 * Maps a legend version resource to the props of its legend.
 *
 * @param resource
 */
export function mapLegendResourceToProps(
  resource: LegendResource,
): Partial<LegendProps> {
  return {
    id: resource.id,
    workspaceId: resource.workspace_id,
    inactive: resource.inactive,
  }
}

/**
 * Maps the outcome of activating or deactivating a legend to its props.
 *
 * @param resource
 */
export function mapLegendActivationResourceToProps(
  resource: LegendActivationResource,
): Partial<LegendProps> {
  return { inactive: resource.inactive }
}

/**
 * Maps the outcome of saving a legend version to the props of the version,
 * which is a draft once saved.
 *
 * @param legendId
 * @param version
 * @param workspaceId
 * @param resource
 */
export function mapSaveLegendVersionResultResourceToProps(
  legendId: Id,
  version: number,
  workspaceId: Id,
  resource: SaveLegendVersionResultResource,
): Partial<LegendVersionProps> {
  return {
    id: legendId,
    version,
    workspaceId,
    published: false,
    concurrencyVersion: resource.concurrency_version,
    dateModified: maybeParseDate(resource.date_modified) ?? undefined,
  }
}

/**
 * Maps a problem the analyzer found in a saved legend.
 *
 * @param resource
 */
export function mapLegendErrorResourceToAnalyzerError(
  resource: LegendErrorResource,
): LegendAnalyzerError {
  return {
    field: resource.field,
    message: resource.message,
    // Only errors block publishing, so a severity the engine adds later must
    // not fail the save that reported it.
    severity: resource.type === 'error' ? 'Error' : 'Warning',
  }
}

/**
 * Maps the personal organizer fields legends can prefill and collect.
 *
 * @param resource
 */
export function mapOrganizerBindingCatalogsResourceToBindings(
  resource: OrganizerBindingCatalogsResource,
): OrganizerBindings {
  const mapCatalog = (
    catalog: OrganizerBindingCatalogsResource['personal']['prefill'],
  ) =>
    Object.entries(catalog).map(([id, binding]) => ({
      id,
      displayName: binding.displayName,
      valueType: typeFromValueType(binding.valueType),
    }))

  return {
    personal: {
      prefill: mapCatalog(resource.personal.prefill),
      collect: mapCatalog(resource.personal.collect),
    },
  }
}
